import * as React from "react";
import { Children, isValidElement, useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from "react";

import { cn } from "@shared/lib/cn";
import { useMergedRef } from "./merge-ref";
import { Thumb } from "./thumb";

/*
 * Меры набора v6 (`SZ.seg` в `liquid-ui.js`): высота, поле дорожки, кегль.
 */
const SIZES = {
  s: [32, 3, 13],
  m: [40, 4, 15],
  l: [48, 4, 16],
} as const;

interface ItemProps {
  value: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Доля сегментов. Сама себя не рисует: её подпись и значение забирает
 * `ToggleGroup` и раскладывает доли сам — плашке нужно знать их число и
 * ширину заранее.
 */
function ToggleGroupItem(props: ItemProps): ReactElement | null {
  void props;
  return null;
}

/**
 * Сегменты по набору v6.
 *
 * Выбор одного из нескольких, а не вкладки: содержимого они не открывают,
 * они задают значение. Дорожка — на материале панелей, у выбранной доли
 * лежит сплошная светлая плашка. Нажали — плашка пружиной подрастает, её
 * можно повести пальцем через доли (на скорости она тянется), подписи под ней
 * загораются на ходу, а отпущенная она встаёт на ближайшую долю. Линзы, как в
 * макете, нет — вариант «В» из проб без стекла, 3 ноября. Движения — в
 * `Thumb` (`shared/ui/thumb.ts`).
 *
 * Доли равной ширины — по самой длинной подписи: путь плашки делится поровну.
 * Объявляются выбором из нескольких (`radiogroup`); стрелки ходят по долям.
 *
 * Значение обязано быть: повторное нажатие по выбранной доле ничего не
 * меняет. `type` принимается ради прежнего вида вызова — выбор всегда один.
 */
function ToggleGroup({
  value,
  onValueChange,
  size = "s",
  height,
  disabled = false,
  className,
  style,
  children,
  ref,
  "aria-label": label,
}: {
  type?: "single";
  value: string;
  onValueChange?: (next: string) => void;
  size?: keyof typeof SIZES;
  /** Своя высота — чтобы встать вровень с полем рядом. */
  height?: number;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  "aria-label"?: string;
}) {
  const [baseH, pad, font] = SIZES[size];
  const H = height ?? baseH;
  const items = Children.toArray(children).filter(
    (child): child is ReactElement<ItemProps> => isValidElement(child) && child.type === ToggleGroupItem,
  );
  const values = items.map((item) => item.props.value);
  const n = Math.max(1, items.length);
  const sel = Math.max(0, values.indexOf(value));

  const root = useRef<HTMLDivElement | null>(null);
  const setRoot = useMergedRef(root, ref);
  const kw = useRef<HTMLSpanElement>(null);
  const knob = useRef<HTMLSpanElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const control = useRef<Thumb | null>(null);
  const [W, setW] = useState(0);
  const latest = useRef({ values, onValueChange, sel });

  useLayoutEffect(() => {
    latest.current = { values, onValueChange, sel };
  });

  // Ширину задают подписи (сетка в потоке); плашке она нужна числом.
  useLayoutEffect(() => {
    const el = root.current!;
    const ro = new ResizeObserver(() => setW(el.offsetWidth));
    ro.observe(el);
    setW(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  const cw = W ? (W - 2 * pad) / n : 0;
  const ch = H - 2 * pad;

  useLayoutEffect(() => {
    if (!cw) return;
    // Подсветка подписей — прямо в узлах: под ведомой плашкой она меняется каждый кадр.
    const lightUp = (i: number) => {
      grid.current?.querySelectorAll<HTMLElement>("[data-opt]").forEach((o, k) => {
        if (k === i) o.setAttribute("data-lit", "");
        else o.removeAttribute("data-lit");
      });
    };
    const c = new Thumb({
      kind: "seg",
      root: root.current!,
      knob: knob.current!,
      wraps: [kw.current!],
      rest: [cw, ch],
      hold: [cw + Math.round(ch * 0.5), ch + Math.round(ch * 0.4)],
      val: latest.current.sel,
      n,
      pad,
      label: lightUp,
      onChange: (i) => {
        const L = latest.current;
        L.onValueChange?.(L.values[i]);
        requestAnimationFrame(() => control.current?.set(latest.current.sel));
      },
    });
    control.current = c;
    return () => {
      c.destroy();
      control.current = null;
    };
  }, [cw, ch, n, pad]);

  useEffect(() => {
    control.current?.set(sel);
  }, [sel]);

  useEffect(() => {
    control.current?.setDisabled(disabled);
  }, [disabled]);

  return (
    <div
      ref={setRoot}
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      data-slot="toggle-group"
      className={cn("lui-seg", className)}
      style={{ height: H, padding: pad, ...style }}
    >
      <span className="lui-seg-bg" aria-hidden />
      <span className="lui-wrap" ref={kw}>
        <span className="lui-seg-knob" ref={knob} style={{ width: cw, height: ch }} />
      </span>
      <div
        ref={grid}
        className="lui-seg-grid"
        style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, height: ch }}
      >
        {items.map((item, i) => (
          <span
            key={item.props.value}
            role="radio"
            aria-checked={i === sel}
            data-opt=""
            data-slot="toggle-group-item"
            data-state={i === sel ? "on" : "off"}
            data-lit={i === sel ? "" : undefined}
            className={cn("lui-seg-opt group/item", item.props.className)}
            style={{ fontSize: font }}
          >
            {item.props.children}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Знак перед подписью доли: `#` у текстового канала, `◍` у голосового.
 *
 * Своим цветом, тише подписи: знак повторяет то, что подпись и так говорит, и
 * спорить с ней в весе ему не за чем. У выбранной доли светлеет вместе с ней.
 */
function ToggleGroupGlyph({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      className={cn("text-[12px] text-dim", "group-data-[state=on]/item:text-link", className)}
      {...props}
    />
  );
}

export { ToggleGroup, ToggleGroupItem, ToggleGroupGlyph };
